import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../shared/MoneyInput";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import PageToolbar from "../../../../design-system/PageToolbar";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { formatCents, formatDate, formatPeriod } from "../shared/money";
import { errorText } from "../api";
import * as api from "./api";

const METHODS = ["linear", "degressive", "gwg", "pool", "none"];
const STATUS_COLOR = { active: "blue", fully_depreciated: "default", disposed: "red" };

const toIso = d => (d ? dayjs(d).format("YYYY-MM-DD") : "");

/** Add / edit modal with the AfA table picker and a live schedule preview. */
const AssetModal = ({ open, asset, table, onClose, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (asset) {
      form.setFieldsValue({
        ...asset,
        acquisitionDate: asset.acquisitionDate ? dayjs(asset.acquisitionDate) : null
      });
    } else {
      form.setFieldsValue({
        method: "linear",
        acquisitionDate: dayjs(),
        salvageValue: 0,
        sonderAfa7gPercent: 0
      });
    }
  }, [open, asset, form]);

  // Useful life follows the AfA table entry until the user overrides it.
  const onTableChange = key => {
    const entry = table.find(e => e.key === key);
    if (entry) form.setFieldsValue({ usefulLifeMonths: entry.months });
  };

  useEffect(() => {
    if (!open || !values?.acquisitionCost) return undefined;
    const handle = setTimeout(async () => {
      try {
        const res = await api.previewSchedule({
          ...values,
          acquisitionDate: toIso(values.acquisitionDate)
        });
        setPreview(res.data);
      } catch {
        setPreview(null);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [open, values]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const body = { ...v, acquisitionDate: toIso(v.acquisitionDate) };
      const res = asset ? await api.updateAsset(asset._id, body) : await api.createAsset(body);
      message.success(t("Saved"));
      onSaved(res.data.asset);
    } catch (error) {
      if (error?.errorFields) return;
      message.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(() => {
    const byGroup = {};
    for (const e of table) (byGroup[e.group] = byGroup[e.group] || []).push(e);
    return Object.entries(byGroup).map(([group, entries]) => ({
      label: t(`afa_group_${group}`),
      options: entries.map(e => ({ value: e.key, label: `${e.label} (${e.years} J.)` }))
    }));
  }, [table, t]);

  const locked = Boolean(asset && (asset.depreciation || []).length);

  return (
    <Modal
      open={open}
      title={asset ? t("Edit asset") : t("Add asset")}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      width={760}
      rootClassName="acct-module"
      okText={t("Save")}
      cancelText={t("Cancel")}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label={t("Name")} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item name="afaTableKey" label={t("AfA table entry")}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={groups}
                onChange={onTableChange}
                placeholder={t("Select from the official AfA table")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="acquisitionDate"
              label={t("Acquisition date")}
              rules={[{ required: true }]}
            >
              <DatePicker format="DD.MM.YYYY" disabled={locked} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="acquisitionCost"
              label={t("Acquisition cost (net)")}
              rules={[{ required: true }]}
            >
              <MoneyInput disabled={locked} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="method" label={t("Depreciation method")}>
              <Select
                disabled={locked}
                options={METHODS.map(m => ({ value: m, label: t(`afa_method_${m}`) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="usefulLifeMonths" label={t("Useful life (months)")}>
              <InputNumber min={1} max={600} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="salvageValue" label={t("Salvage value")}>
              <MoneyInput />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="sonderAfa7gPercent" label={t("Special depreciation § 7g (%)")}>
              <InputNumber min={0} max={40} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label={t("Notes")}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
      {preview ? (
        <Card size="small" title={t("AfA preview")}>
          <Row gutter={[16, 16]} className="acct-kpi">
            <Col xs={12} sm={8}>
              <Statistic title={t("Monthly AfA")} value={formatCents(preview.monthly)} />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic title={t("First year")} value={formatCents(preview.firstYear)} />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic title={t("Total")} value={formatCents(preview.total)} />
            </Col>
            <Col xs={12} sm={8}>
              <Statistic title={t("Last period")} value={formatPeriod(preview.endPeriod)} />
            </Col>
            <Col xs={24} sm={16}>
              <Statistic
                title={t("Suggested method")}
                value={t(`afa_method_${preview.suggestedMethod}`)}
              />
            </Col>
          </Row>
          {values?.method === "degressive" && !preview.degressiveAllowed ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBlockStart: 8 }}
              message={t(
                "Degressive depreciation is only allowed for movable assets acquired between 01.07.2025 and 31.12.2027."
              )}
            />
          ) : null}
        </Card>
      ) : null}
    </Modal>
  );
};

const DisposeModal = ({ open, asset, onClose, onDone }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open)
      form.setFieldsValue({ date: dayjs(), proceeds: 0, taxable: true, privateWithdrawal: false });
  }, [open, form]);
  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const res = await api.disposeAsset(asset._id, { ...v, date: toIso(v.date) });
      const r = res.data;
      message.success(
        `${t("Disposal booked")}: ${r.gain ? `${t("Book gain")} ${formatCents(r.gain)}` : `${t("Book loss")} ${formatCents(r.loss)}`}`
      );
      onDone();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open={open}
      title={`${t("Dispose asset")}: ${asset?.name || ""}`}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      rootClassName="acct-module"
      okText={t("Book disposal")}
      cancelText={t("Cancel")}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="date" label={t("Disposal date")} rules={[{ required: true }]}>
          <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="proceeds" label={t("Sale proceeds (net)")}>
          <MoneyInput />
        </Form.Item>
        <Form.Item name="proceedsAccount" label={t("Received on account (number, default bank)")}>
          <Input placeholder="1200" />
        </Form.Item>
        <Form.Item name="taxable" label={t("With 19 % VAT")} valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="privateWithdrawal"
          label={t("Private withdrawal (no sale)")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">
        {t(
          "The depreciation up to the disposal month is posted first; the remaining book value becomes a book gain or loss."
        )}
      </Typography.Text>
    </Modal>
  );
};

const AnlagenspiegelModal = ({ open, onClose }) => {
  const { t } = useTranslation("accounting");
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    api
      .anlagenspiegel(year)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)));
  }, [open, year]);
  const money = key => ({
    dataIndex: key,
    align: "end",
    render: v => <span className="acct-num">{formatCents(v)}</span>
  });
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      rootClassName="acct-module"
      title={t("Asset schedule (Anlagenspiegel)")}
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <span>{t("Year")}</span>
        <InputNumber value={year} onChange={setYear} min={2000} max={2100} />
      </Space>
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        pagination={false}
        rowKey="account"
        dataSource={data?.rows || []}
        summary={() =>
          data ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>{t("Total")}</Table.Summary.Cell>
              {[
                "openingCost",
                "additions",
                "disposals",
                "closingCost",
                "openingDepreciation",
                "depreciation",
                "closingDepreciation",
                "closingBookValue"
              ].map((k, i) => (
                <Table.Summary.Cell index={i + 1} key={k} align="end">
                  <span className="acct-num acct-num--strong">{formatCents(data.totals[k])}</span>
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          ) : null
        }
        columns={[
          { title: t("Account"), render: (_, r) => `${r.account} ${r.name}` },
          { title: t("Opening cost"), ...money("openingCost") },
          { title: t("Additions"), ...money("additions") },
          { title: t("Disposals"), ...money("disposals") },
          { title: t("Closing cost"), ...money("closingCost") },
          { title: t("Opening depreciation"), ...money("openingDepreciation") },
          { title: t("Depreciation of the year"), ...money("depreciation") },
          { title: t("Closing depreciation"), ...money("closingDepreciation") },
          { title: t("Closing book value"), ...money("closingBookValue") }
        ]}
      />
      <Typography.Title level={5} style={{ marginBlockStart: 16 }}>
        {t("Assets")}
      </Typography.Title>
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        pagination={false}
        rowKey="_id"
        dataSource={data?.assets || []}
        columns={[
          { title: t("No."), dataIndex: "assetNumber" },
          { title: t("Name"), dataIndex: "name" },
          { title: t("Acquired"), dataIndex: "acquisitionDate", render: formatDate },
          { title: t("Opening book value"), ...money("openingBookValue") },
          { title: t("Addition"), ...money("addition") },
          { title: t("AfA"), ...money("depreciation") },
          { title: t("Disposal"), ...money("disposal") },
          { title: t("Closing book value"), ...money("closingBookValue") }
        ]}
      />
    </Modal>
  );
};

/** Posting details of one asset: expanded row on desktop, a dialog on phones. */
const AssetDetails = ({ asset: a }) => {
  const { t } = useTranslation("accounting");
  return (
    <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
      <Descriptions.Item label={t("Depreciation account")}>
        {a.depreciationAccount}
      </Descriptions.Item>
      <Descriptions.Item label={t("Monthly AfA")}>
        <Amount cents={a.schedule?.monthly} />
      </Descriptions.Item>
      <Descriptions.Item label={t("Last period")}>
        {formatPeriod(a.schedule?.endPeriod)}
      </Descriptions.Item>
      <Descriptions.Item label={t("Posted periods")}>
        <span className="acct-wrap">
          {(a.depreciation || []).map(d => formatPeriod(d.period)).join(", ") || "-"}
        </span>
      </Descriptions.Item>
      {a.disposal?.date ? (
        <Descriptions.Item label={t("Disposal")}>
          {formatDate(a.disposal.date)} · <Amount cents={a.disposal.proceeds} />
        </Descriptions.Item>
      ) : null}
      {a.notes ? (
        <Descriptions.Item label={t("Notes")}>
          <span className="acct-wrap">{a.notes}</span>
        </Descriptions.Item>
      ) : null}
    </Descriptions>
  );
};

const AssetsTab = () => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [details, setDetails] = useState(null);
  const [assets, setAssets] = useState([]);
  const [table, setTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(false);
  const [disposing, setDisposing] = useState(null);
  const [spiegel, setSpiegel] = useState(false);
  const [runPeriod, setRunPeriod] = useState(dayjs().subtract(1, "month"));
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listAssets();
      setAssets(Array.isArray(res.data?.assets) ? res.data.assets : []);
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .afaTable()
      .then(res => setTable(Array.isArray(res.data?.entries) ? res.data.entries : []))
      .catch(() => setTable([]));
  }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.runDepreciation(runPeriod.format("YYYY-MM"));
      const r = res.data;
      message.success(
        `${t("Depreciation posted")}: ${r.posted} (${formatCents(r.amount)}), ${t("skipped")}: ${r.skipped}`
      );
      if (r.errors?.length)
        message.warning(r.errors.map(e => `${e.name}: ${e.message}`).join("; "));
      load();
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setRunning(false);
    }
  };

  const remove = async id => {
    try {
      await api.deleteAsset(id);
      message.success(t("Deleted"));
      load();
    } catch (error) {
      message.error(errorText(error));
    }
  };

  return (
    <div>
      <PageToolbar
        filters={
          <>
            <DatePicker picker="month" value={runPeriod} onChange={v => v && setRunPeriod(v)} />
            <Button onClick={run} loading={running}>
              {t("Run depreciation for month")}
            </Button>
          </>
        }
        actions={
          <>
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setModal(true);
              }}
            >
              {t("Add asset")}
            </Button>
            <Button onClick={() => setSpiegel(true)}>{t("Asset schedule (Anlagenspiegel)")}</Button>
          </>
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={assets}
        size="small"
        // Phones show cards; the posting details open in a dialog there instead.
        expandable={isMobile ? undefined : { expandedRowRender: a => <AssetDetails asset={a} /> }}
        columns={[
          { title: t("No."), dataIndex: "assetNumber", width: 90 },
          { title: t("Name"), dataIndex: "name", mobile: "title" },
          { title: t("Account"), dataIndex: "account", width: 80 },
          { title: t("Acquired"), dataIndex: "acquisitionDate", render: formatDate, width: 110 },
          {
            title: t("Acquisition cost"),
            dataIndex: "acquisitionCost",
            align: "end",
            render: v => <Amount cents={v} />
          },
          {
            title: t("Method"),
            dataIndex: "method",
            render: m => t(`afa_method_${m}`),
            width: 110
          },
          {
            title: t("Useful life"),
            dataIndex: "usefulLifeMonths",
            render: m => (m ? `${m} ${t("months")}` : "-"),
            width: 110
          },
          {
            title: t("Book value"),
            dataIndex: "bookValue",
            align: "end",
            mobile: "subtitle",
            render: v => <Amount cents={v} strong large={isMobile} />
          },
          {
            title: t("Status"),
            dataIndex: "status",
            render: s => <Tag color={STATUS_COLOR[s]}>{t(`asset_status_${s}`)}</Tag>,
            width: 140
          },
          {
            title: "",
            key: "actions",
            width: 200,
            render: (_, a) => (
              <Space wrap>
                {isMobile ? (
                  <Button size="small" onClick={() => setDetails(a)}>
                    {t("Details")}
                  </Button>
                ) : null}
                {a.status !== "disposed" ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setEditing(a);
                      setModal(true);
                    }}
                  >
                    {t("Edit")}
                  </Button>
                ) : null}
                {a.status !== "disposed" ? (
                  <Button size="small" onClick={() => setDisposing(a)}>
                    {t("Dispose")}
                  </Button>
                ) : null}
                {!(a.depreciation || []).length && a.status !== "disposed" ? (
                  <Popconfirm title={t("Delete this asset?")} onConfirm={() => remove(a._id)}>
                    <Button size="small" danger>
                      {t("Delete")}
                    </Button>
                  </Popconfirm>
                ) : null}
              </Space>
            )
          }
        ]}
      />
      <Modal
        rootClassName="acct-module"
        open={Boolean(details)}
        title={details?.name}
        onCancel={() => setDetails(null)}
        rootClassName="acct-module"
        footer={<Button onClick={() => setDetails(null)}>{t("Close")}</Button>}
      >
        {details ? <AssetDetails asset={details} /> : null}
      </Modal>
      <AssetModal
        open={modal}
        asset={editing}
        table={table}
        onClose={() => setModal(false)}
        onSaved={() => {
          setModal(false);
          load();
        }}
      />
      <DisposeModal
        open={Boolean(disposing)}
        asset={disposing}
        onClose={() => setDisposing(null)}
        onDone={() => {
          setDisposing(null);
          load();
        }}
      />
      <AnlagenspiegelModal open={spiegel} onClose={() => setSpiegel(false)} />
    </div>
  );
};

export default AssetsTab;
